import { PlusIcon } from "@xola/icons";
import { Button, PopoverList } from "@xola/ui-kit";
import clsx from "clsx";
import { getAddOns } from "common";
import React, { useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    AddOn,
    CatalogItem,
    CatalogItemDisplaySection,
    CatalogItemObject,
    CatalogType,
    Experience,
    LineItemType,
} from "types";
import { api } from "../../../../api/api";
import { useAutoFetchCollection } from "../../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { NoTranslate } from "../../../Common/NoTranslate";
import { Loader } from "../../../Loader";
import { updateAddOnFormFields } from "./AddOnFormUtils";
import { AddOnEditorFormState } from "./AddOnTypes";
import { EditAddOnModal } from "./EditAddOnModal";
import { OverrideQuestionnaireModal } from "./OverrideQuestionnaireModal";
import { useForm, useSaveForm, useSaveQuestionnaireForSavedViews } from "@/hooks/questionnaireFormHooks";
import { useModal } from "@/hooks/useVisibility";
import { sanitizeExperiencePayload } from "@/models/Experience";
import { getSeller } from "@/utils/getSeller";

export const ExperienceCreateAddOnPopover = ({ experience }: { experience: Experience }) => {
    const { data: globalAddOns, isLoading } = useAutoFetchCollection<AddOn>("catalogItems", {
        "unitType[in]": [LineItemType.ADDON, LineItemType.PICKUP].join(","),
        object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
        "type[nin]": CatalogType.CHOICES_ITEM,
    });

    const seller = getSeller();
    const popover = useModal();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showOverrideModal, setShowOverrideModal] = useState(false);
    const [pendingAddOn, setPendingAddOn] = useState<AddOn | null>(null);
    const [pendingFormValues, setPendingFormValues] = useState<AddOnEditorFormState | undefined>();

    const { form: questionnaireForm, globalForm: globalQuestionnaire } = useForm(experience?.id);
    const { mutateAsync: saveForm } = useSaveForm();
    const saveQuestionnaireForSavedViews = useSaveQuestionnaireForSavedViews();

    const invalidate = useInvalidate();

    const remainingAddOns = useMemo(() => {
        const experienceAddOns = getAddOns(experience) as AddOn[];
        const experienceAddOnIds = new Set(experienceAddOns.map((addOn: AddOn) => addOn.parent?.id));
        return globalAddOns.filter(
            (addOn: AddOn) =>
                !!addOn.type && addOn.type !== CatalogType.CHOICES_ITEM && !experienceAddOnIds.has(addOn.id),
        );
    }, [experience, globalAddOns]);

    const shouldShowOverrideModal = (formValues?: AddOnEditorFormState) => {
        const hasQuestionnaire = formValues?.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
        const hasGlobalQuestionnaire = !!globalQuestionnaire?.fields?.length;
        const hasProductQuestionnaire = !!questionnaireForm?.fields?.length;

        return hasQuestionnaire && hasGlobalQuestionnaire && !hasProductQuestionnaire;
    };

    const executeAddOnLink = async (addOn: AddOn, formValues?: AddOnEditorFormState) => {
        const response = await linkAddOnToExperience(addOn);

        // Update corresponding addon field in form for roster settings
        if (formValues?.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE)) {
            await updateAddOnFormFields({
                catalogItem: response.data,
                formValues,
                globalQuestionnaire,
                questionnaireForm,
                experience,
                saveForm,
                saveQuestionnaireForSavedViews,
            });
        }

        await api.put(`/experiences/${experience?.id}`, sanitizeExperiencePayload(experience));
        popover.hide();
        invalidate("catalogItems");
        invalidate("experiences", experience.id);
        invalidate("purchaseRuleLinks");
        invalidate("forms");
    };

    const handleClick = async (addOn: AddOn, formValues?: AddOnEditorFormState) => {
        if (shouldShowOverrideModal(formValues)) {
            setPendingAddOn(addOn);
            setPendingFormValues(formValues);
            setShowOverrideModal(true);
            return;
        }

        await executeAddOnLink(addOn, formValues);
    };

    const { mutateAsync: linkAddOnToExperience, isLoading: isLinkingAddOn } = useMutation(async (addOn: AddOn) =>
        linkGlobalAddOnToExperience(addOn, experience, seller.id),
    );

    const handleOverrideConfirm = async () => {
        setShowOverrideModal(false);
        if (pendingAddOn && pendingFormValues) {
            await executeAddOnLink(pendingAddOn, pendingFormValues);
        }
        setPendingAddOn(null);
        setPendingFormValues(undefined);
    };

    const handleOverrideCancel = () => {
        setShowOverrideModal(false);
        setPendingAddOn(null);
        setPendingFormValues(undefined);
    };

    const handleCreateClick = () => {
        setShowCreateModal(true);
        popover.hide();
    };

    return (
        <>
            <PopoverList
                offset={[0, 18]}
                visible={popover.isVisible}
                className="max-h-96 w-75 overflow-y-auto"
                onClickOutside={popover.hide}
            >
                <Button
                    icon={<PlusIcon />}
                    disabled={isLinkingAddOn}
                    onClick={popover.isVisible ? popover.hide : popover.show}
                >
                    Create Add-on
                </Button>

                {isLinkingAddOn && (
                    <PopoverList.Item className="flex justify-center">
                        <Loader />
                    </PopoverList.Item>
                )}

                {isLoading ? (
                    <Loader />
                ) : globalAddOns.length > 0 && !isLinkingAddOn ? (
                    remainingAddOns.map((addOn: AddOn, index) => (
                        <PopoverList.Item
                            key={addOn.id}
                            name={addOn.name}
                            className={clsx(index === remainingAddOns.length - 1 && "h-13")}
                            onClickItem={async () => handleClick(addOn)}
                        >
                            <NoTranslate>{addOn.name}</NoTranslate>
                        </PopoverList.Item>
                    ))
                ) : null}

                <PopoverList.Item name="newAddOn" className="sticky bottom-0 bg-white" onClickItem={handleCreateClick}>
                    <Button variant="outline" color="secondary" icon={<PlusIcon />} className="w-full">
                        New Add-on
                    </Button>
                </PopoverList.Item>
            </PopoverList>
            <EditAddOnModal
                isOpen={showCreateModal}
                afterCreate={async (addOn, formValues) => handleClick(addOn, formValues)}
                currentPageExperience={experience}
                onClose={() => setShowCreateModal(false)}
            />
            <OverrideQuestionnaireModal
                isOpen={showOverrideModal}
                onClose={handleOverrideCancel}
                onConfirm={handleOverrideConfirm}
            />
        </>
    );
};

export const linkGlobalAddOnToExperience = async (
    addOn: CatalogItem,
    experience: Experience,
    sellerId: string,
    displaySections?: CatalogItemDisplaySection[],
) => {
    return api.post<CatalogItem>(`/catalogItems`, {
        object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
        product: { id: experience.id },
        parent: { id: addOn.id },
        seller: { id: sellerId },
        displaySections: displaySections,
    });
};
